import { useCallback, useEffect, useRef, useState } from "react"
import Axios from "axios"
import { useLocation } from "react-router-dom"
import { PATH } from "constants/path"
import {
  encryptionController,
  EncryptionKeys,
} from "controllers/EncryptionController"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { getPrivateKeyByUserId } from "helpers/utils"
import {
  checkVaultAndStorePrivateKey,
  ensurePrivateKeyExists,
  autoFixProjectKey,
  autoFixBuildKey,
  autoFixConversationKey,
} from "services/keyHealthService"
import { getAccessTokenInLocalStorage } from "helpers/access_token"
import {
  BannerStatus,
  FailedKey,
  KeyFixProgress,
  KeyHealthScope,
} from "types/keyHealth"
import { getProjectComponentDetailMiddleware } from "pages/project-component/services/api"
import { getHistoriesBuildComponentConvoMiddleware } from "pages/project-component-detail/services"
import { GetKeysType, KeyEncryptionType } from "types"

// Kết quả health check được cache trong 60 giây để tránh gọi API thừa
// khi user navigate nhanh giữa các routes.
const CACHE_TTL_MS = 60_000

// Re-check sau khi tab đã ẩn hơn 5 phút — phát hiện permission changes
// xảy ra trong lúc tab không active.
const TAB_HIDDEN_THRESHOLD_MS = 5 * 60 * 1000

interface UseKeyHealthCheckOptions {
  userId: string
  isEncrypted: boolean
}

interface UseKeyHealthCheckReturn {
  bannerStatus: BannerStatus
  bannerScope: KeyHealthScope
  fixProgress: KeyFixProgress[]
  isAutoFixing: boolean
  isDetailModalOpen: boolean
  openDetailModal: () => void
  closeDetailModal: () => void
  needsVaultRecovery: boolean
  onVaultRecoverySuccess: () => void
  requestVaultRecovery: () => void
  needsPrivateKeyRecovery: boolean
  onPrivateKeyRecoverySuccess: () => void
  isPreCheckReady: boolean
  keyAvailability: {
    project: Record<string, boolean>
    build: Record<string, boolean>
    conversation: Record<string, boolean>
  }
}

const hasConversationPreCheckParams = (pathname: string, search: string) => {
  if (!pathname.includes("conversations")) {
    return false
  }
  const searchParams = new URLSearchParams(
    search || (pathname.includes("?") ? pathname.split("?")[1] : "")
  )
  const conversationId = searchParams.get("conversationId")
  const workspaceId = searchParams.get("workspaceId")
  return Boolean(conversationId && workspaceId)
}

const hasBuildPreCheckParams = (pathname: string) => {
  if (!pathname.includes("build")) {
    return false
  }
  const buildId = pathname.match(/\/build\/[^/]+\/[^/]+\/([^/]+)/)?.[1]
  const projectId = pathname.match(/\/build\/[^/]+\/([^/]+)\/[^/]+/)?.[1]
  return Boolean(buildId && projectId)
}

const detectScopeFromPath = (pathname: string): KeyHealthScope => {
  if (pathname.includes(PATH.projects)) return "projects"
  if (pathname.includes("project-detail")) return "projectDetail"
  if (pathname.includes("project/build-component")) return "buildComponent"
  if (
    pathname.includes("project/component") ||
    pathname.includes("invitees/component")
  )
    return "projectComponent"
  if (hasBuildPreCheckParams(pathname)) return "build"
  return "global"
}

export const useKeyHealthCheck = ({
  userId,
  isEncrypted,
}: UseKeyHealthCheckOptions): UseKeyHealthCheckReturn => {
  const location = useLocation()
  const [bannerStatus, setBannerStatus] = useState<BannerStatus>("hidden")
  const [bannerScope, setBannerScope] = useState<KeyHealthScope>(() =>
    detectScopeFromPath(location.pathname)
  )
  const [fixProgress, setFixProgress] = useState<KeyFixProgress[]>([])
  const [isAutoFixing, setIsAutoFixing] = useState(false)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [needsVaultRecovery, setNeedsVaultRecovery] = useState(false)
  const [needsPrivateKeyRecovery, setNeedsPrivateKeyRecovery] = useState(false)
  const [keyAvailability, setKeyAvailability] = useState<{
    project: Record<string, boolean>
    build: Record<string, boolean>
    conversation: Record<string, boolean>
  }>({ project: {}, build: {}, conversation: {} })

  // false khi đang chờ worker check cho projectDetail / projectComponent
  // → PageLayout sẽ block render children cho đến khi true
  const initialNeedsPreCheck =
    location.pathname.includes("project-detail") ||
    location.pathname.includes("project/component") ||
    location.pathname.includes("project/build-component") ||
    hasBuildPreCheckParams(location.pathname) ||
    location.pathname.includes("invitees/component") ||
    hasConversationPreCheckParams(location.pathname, location.search)
  const [isPreCheckReady, setIsPreCheckReady] = useState(!initialNeedsPreCheck)

  const performCheckRef = useRef<(forceRefresh?: boolean) => void>()
  const autoFixRunningRef = useRef(false)
  const hasHandledFirstPathEffectRef = useRef(false)

  const workerRef = useRef<Worker | null>(null)
  const handleWorkerMessageRef = useRef<((e: MessageEvent) => void) | null>(
    null
  )
  const pendingCheckIdsRef = useRef<{
    projectId?: string
    buildId?: string
    conversationId?: string
  }>({})
  const workerCheckInFlightRef = useRef(false)

  // Phase 1.4 — Dedup guard: không chạy concurrent health checks
  const isCheckingRef = useRef(false)

  // Phase 1.2 — 60-second result cache
  const checkResultCacheRef = useRef<{
    timestamp: number
    pathname: string
  } | null>(null)

  // Phase 1.7 — Track khi tab bị ẩn để chỉ re-check sau >5 phút
  const lastHiddenTimeRef = useRef<number>(0)

  /**
   * Run auto-fix for a list of failed keys sequentially.
   * Each key gets its own API call so the user can watch per-key progress.
   */
  const runAutoFix = useCallback(
    async (keys: FailedKey[]) => {
      if (autoFixRunningRef.current || keys.length === 0) return
      const keyTypePriority: Record<
        "project" | "build" | "conversation",
        number
      > = {
        project: 0,
        build: 1,
        conversation: 2,
      }
      const getKeyPriority = (keyType?: FailedKey["keyType"]) => {
        if (!keyType || !(keyType in keyTypePriority)) {
          return Number.MAX_SAFE_INTEGER
        }
        return keyTypePriority[keyType as "project" | "build" | "conversation"]
      }
      const sortedKeys = [...keys].sort(
        (a, b) => getKeyPriority(a.keyType) - getKeyPriority(b.keyType)
      )
      autoFixRunningRef.current = true
      setIsAutoFixing(true)

      // Initialise progress — all keys start as "pending"
      setFixProgress(
        sortedKeys.map((k) => ({ ...k, status: "pending" as const }))
      )

      const finalResults: KeyFixProgress[] = sortedKeys.map((k) => ({
        ...k,
        status: "pending" as const,
      }))

      for (let i = 0; i < sortedKeys.length; i++) {
        const key = sortedKeys[i]

        // Mark this key as actively being fixed
        setFixProgress((prev) =>
          prev.map((p) => (p.id === key.id ? { ...p, status: "fixing" } : p))
        )

        // Route auto-fix theo keyType
        let success = false
        if (key.keyType === "build") {
          ;({ success } = await autoFixBuildKey(
            key.id,
            userId,
            key.workspaceId
          ))
        } else if (key.keyType === "conversation") {
          ;({ success } = await autoFixConversationKey(
            key.id,
            userId,
            key.workspaceId
          ))
        } else {
          ;({ success } = await autoFixProjectKey(key.id, userId))
        }

        const resolved = success ? "success" : "error"
        finalResults[i] = { ...key, status: resolved }

        setFixProgress((prev) =>
          prev.map((p) => (p.id === key.id ? { ...p, status: resolved } : p))
        )
      }

      autoFixRunningRef.current = false
      setIsAutoFixing(false)

      // If every key was fixed successfully → turn banner green, close modal
      // (keep fixProgress so user can re-open via "View details" on green banner)
      const allFixed = finalResults.every((r) => r.status === "success")
      if (allFixed) {
        setBannerStatus("green")
        setIsDetailModalOpen(false)
      }
      // Else: banner stays yellow; user can see errors in the modal
    },
    [userId]
  )

  // ─── Send check to worker ──────────────────────────────────────────────────

  const sendCheckToWorker = useCallback(
    (config: {
      checkProject?: boolean
      checkBuild?: boolean
      checkConversation?: boolean
      projectId?: string
      buildId?: string
      conversationId?: string
      workspaceId?: string
    }) => {
      // Always update pending IDs before the in-flight guard so that when a
      // stale worker result arrives it builds keyAvailability for the current
      // page, not the page the worker was originally dispatched for.
      pendingCheckIdsRef.current = {
        projectId: config.projectId,
        buildId: config.buildId,
        conversationId: config.conversationId,
      }

      const worker = workerRef.current
      if (!worker || !userId || workerCheckInFlightRef.current) return

      const accessToken = getAccessTokenInLocalStorage()
      const vaultKey = localStorage.getItem(EncryptionKeys.userVaultKey) || ""
      const localProjectKeys: Record<string, string> = JSON.parse(
        localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
      )
      const localBuildKeys: Record<string, string> = JSON.parse(
        localStorage.getItem(
          EncryptionKeys.buildAdditionalInfoEncryptionKeys
        ) || "{}"
      )
      const localConversationKeys: Record<string, string> = JSON.parse(
        localStorage.getItem(EncryptionKeys.conversationEncryptionKeys) || "{}"
      )

      workerCheckInFlightRef.current = true
      setBannerStatus("checking")

      worker.postMessage({
        type: "CHECK_KEYS",
        payload: {
          checkProject: config.checkProject ?? false,
          checkBuild: config.checkBuild ?? false,
          checkConversation: config.checkConversation ?? false,
          projectId: config.projectId,
          buildId: config.buildId,
          conversationId: config.conversationId,
          workspaceId: config.workspaceId,
          userId,
          vaultKey,
          accessToken,
          localProjectKeys,
          localBuildKeys,
          localConversationKeys,
        },
      })
    },
    [userId]
  )

  // ─── Invitees component check (main thread — requires shareKey logic) ──────

  const getInviteesRouteInfo = useCallback((pathname: string) => {
    const inviteesMatch = pathname.match(
      /^\/invitees\/component\/([^/]+)\/[^/]+\/([^/]+)\/(?:(?:[^/]+)\/)?([^/]+)\/([^/]+)$/
    )
    if (!inviteesMatch) return null
    return {
      componentId: inviteesMatch[1],
      projectBuildComponentId: inviteesMatch[2],
      conversationId: inviteesMatch[3],
      dbName: inviteesMatch[4],
    }
  }, [])

  const getInviteeProjectKeyByDbName = useCallback(
    async (projectId: string, dbName: string): Promise<string> => {
      try {
        const response = await Axios.get(`/api/auth/keys?loading=0`, {
          params: {
            type: GetKeysType.ProjectKey,
            relation_id: projectId,
          },
          headers: {
            "x-db-key": dbName,
          },
        })
        const firstProjectKey = response?.data?.data?.[0]
        if (!firstProjectKey?.encrypted_key) {
          return ""
        }

        if (firstProjectKey.type === KeyEncryptionType.ProjectShareKey) {
          const privateKey: any = await getPrivateKeyByUserId()
          if (!privateKey) return ""
          return privateKey.decrypt(firstProjectKey.encrypted_key, "RSA-OAEP")
        }

        const vaultKey = localStorage.getItem(EncryptionKeys.userVaultKey) || ""
        if (!vaultKey) return ""
        const decrypted = (await encryptionGCMHelper.decryptContext(
          vaultKey,
          firstProjectKey.encrypted_key
        )) as string
        if (!decrypted || decrypted === firstProjectKey.encrypted_key) {
          return ""
        }
        return decrypted
      } catch {
        return ""
      }
    },
    []
  )

  const getCheckInviteesComponent = useCallback(
    async (pathname: string) => {
      setBannerStatus("checking")
      const routeInfo = getInviteesRouteInfo(pathname)
      if (!routeInfo) {
        setBannerStatus("hidden")
        return
      }

      const { componentId, projectBuildComponentId, conversationId, dbName } =
        routeInfo

      const componentDetail = await getProjectComponentDetailMiddleware(
        componentId,
        conversationId,
        dbName
      )
      const projectId = componentDetail?.project_id || ""
      if (!projectId) {
        setBannerStatus("yellow")
        return
      }

      const histories = await getHistoriesBuildComponentConvoMiddleware(
        projectId,
        projectBuildComponentId,
        conversationId,
        dbName
      )
      const firstHistory = histories?.[0]

      const shareKey = firstHistory.shareKey
      let decryptedShareKey = ""

      if (!firstHistory.is_owner) {
        try {
          const privateKey: any = await getPrivateKeyByUserId()
          if (!privateKey) {
            setBannerStatus("yellow")
            return
          }
          decryptedShareKey = privateKey.decrypt(shareKey, "RSA-OAEP")
        } catch {
          setBannerStatus("yellow")
          return
        }
      } else {
        const projectEncryptionKeys: Record<string, string> = JSON.parse(
          localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
        )
        let projectKey = projectEncryptionKeys[projectId]

        if (!projectKey) {
          projectKey = await getInviteeProjectKeyByDbName(projectId, dbName)
          if (projectKey) {
            projectEncryptionKeys[projectId] = projectKey
            localStorage.setItem(
              EncryptionKeys.projectEncryptionKeys,
              JSON.stringify(projectEncryptionKeys)
            )
          }
        }

        if (!projectKey) {
          setBannerStatus("yellow")
          return
        }

        try {
          if (shareKey) {
            decryptedShareKey = (await encryptionGCMHelper.decryptContext(
              projectKey,
              shareKey
            )) as string
          } else {
            decryptedShareKey = projectKey
          }
        } catch {
          setBannerStatus("yellow")
          return
        }
      }

      if (!decryptedShareKey || decryptedShareKey === shareKey) {
        setBannerStatus("yellow")
        return
      }

      setFixProgress([])
      setBannerStatus("green")
    },
    [getInviteeProjectKeyByDbName, getInviteesRouteInfo]
  )

  // ─── Core check orchestrator ───────────────────────────────────────────────

  const performCheck = useCallback(
    async (forceRefresh = false) => {
      if (!userId) return

      // Dedup guard: không chạy concurrent health checks
      if (isCheckingRef.current) return
      isCheckingRef.current = true

      try {
        const pathname = location.pathname
        const routeKey = `${location.pathname}${location.search}`

        // 60-second cache: bỏ qua nếu cùng route và chưa hết TTL
        if (!forceRefresh && checkResultCacheRef.current) {
          const age = Date.now() - checkResultCacheRef.current.timestamp
          if (
            age < CACHE_TTL_MS &&
            checkResultCacheRef.current.pathname === routeKey
          ) {
            // Cache hit: unblock content nếu đang chờ pre-check
            setIsPreCheckReady(true)
            return
          }
        }

        // Content chưa được encrypt → gray banner, không cần kiểm tra keys
        if (!isEncrypted) {
          setBannerScope("global")
          setBannerStatus("gray")
          setIsPreCheckReady(true)
          checkResultCacheRef.current = {
            timestamp: Date.now(),
            pathname: routeKey,
          }
          return
        }

        const isProject = pathname.includes(PATH.projects)
        const isProjectDetail = pathname.includes(`project-detail`)
        const isComponent = pathname.includes(`project/component`)
        const isBuild = pathname.includes(`build`)
        const isBuildComponent = pathname.includes(`project/build-component`)
        const isInviteesComponent = pathname.includes(`invitees/component`)
        const isConversations = pathname.includes(`conversations`)

        // Set scope trước gate checks để overlay hiển thị đúng message ngay từ đầu
        if (isProject) setBannerScope("projects")
        else if (isProjectDetail) setBannerScope("projectDetail")
        else if (isBuildComponent) setBannerScope("buildComponent")
        else if (isComponent || isInviteesComponent)
          setBannerScope("projectComponent")
        else if (isBuild) setBannerScope("build")
        else setBannerScope("global")

        // Gate 1: vault key format + fetch & store private key
        const { vaultKeyOk } = await checkVaultAndStorePrivateKey(userId)
        if (!vaultKeyOk) {
          setNeedsVaultRecovery(true)
          setBannerStatus("hidden")
          setIsPreCheckReady(true)
          return
        }

        // Gate 2: private key must exist in IndexedDB
        const { privateKeyOk } = await ensurePrivateKeyExists(userId)
        if (!privateKeyOk) {
          const vaultKey =
            localStorage.getItem(EncryptionKeys.userVaultKey) || ""
          if (encryptionController().isValidKeyEncryption(vaultKey)) {
            setNeedsPrivateKeyRecovery(true)
          } else {
            setNeedsVaultRecovery(true)
          }
          setBannerStatus("hidden")
          setIsPreCheckReady(true)
          return
        }

        if (isProject) {
          setBannerScope("projects")
          sendCheckToWorker({ checkProject: true })
        } else if (isProjectDetail) {
          setBannerScope("projectDetail")
          const projectComponentPrefix = PATH.projectComponent.split("/:")[0]
          const match = pathname.match(
            new RegExp(`${projectComponentPrefix}/([^/]+)`)
          )
          const projectId = match?.[1]
          sendCheckToWorker({
            checkProject: true,
            projectId,
          })
        } else if (isComponent) {
          setBannerScope("projectComponent")
          const componentMatch = pathname.match(/\/project\/component\/([^/]+)/)
          const componentId = componentMatch?.[1] || ""
          if (!componentId) {
            setBannerStatus("hidden")
            setIsPreCheckReady(true)
            return
          }
          const detail = await getProjectComponentDetailMiddleware(componentId)
          if (!detail) {
            setBannerStatus("hidden")
            setIsPreCheckReady(true)
            return
          }
          sendCheckToWorker({
            checkProject: true,
            projectId: detail.project_id,
          })
        } else if (isBuildComponent) {
          setBannerScope("buildComponent")
          const componentMatch = pathname.match(
            /\/project\/build-component\/([^/]+)/
          )
          const componentId = componentMatch?.[1] || ""
          if (!componentId) {
            setBannerStatus("hidden")
            setIsPreCheckReady(true)
            return
          }
          const detail = await getProjectComponentDetailMiddleware(componentId)
          if (!detail) {
            setBannerStatus("hidden")
            setIsPreCheckReady(true)
            return
          }
          sendCheckToWorker({
            checkProject: true,
            projectId: detail.project_id,
          })
        } else if (isBuild) {
          setBannerScope("build")
          const buildId = pathname.match(/\/build\/[^/]+\/[^/]+\/([^/]+)/)?.[1]
          const projectId = pathname.match(
            /\/build\/[^/]+\/([^/]+)\/[^/]+/
          )?.[1]
          if (buildId && projectId) {
            setIsPreCheckReady(false)
            sendCheckToWorker({
              checkProject: true,
              checkBuild: true,
              projectId,
              buildId,
            })
          } else {
            setBannerStatus("hidden")
          }
        } else if (isConversations) {
          const searchParams = new URLSearchParams(
            location.search ||
              (pathname.includes("?") ? pathname.split("?")[1] : "")
          )
          const conversationId = searchParams.get("conversationId") || undefined
          const workspaceId = searchParams.get("workspaceId") || undefined
          setBannerScope("global")
          if (conversationId && workspaceId) {
            setIsPreCheckReady(false)
            sendCheckToWorker({
              checkConversation: true,
              conversationId,
              workspaceId,
            })
          } else {
            setBannerStatus("hidden")
            setIsPreCheckReady(true)
          }
        } else if (isInviteesComponent) {
          setBannerScope("projectComponent")
          await getCheckInviteesComponent(pathname)
          setIsPreCheckReady(true)
        } else {
          setBannerScope("global")
          setBannerStatus("hidden")
        }

        // Cập nhật cache sau khi dispatch scope check
        checkResultCacheRef.current = {
          timestamp: Date.now(),
          pathname: routeKey,
        }
      } finally {
        isCheckingRef.current = false
      }
    },
    [
      userId,
      isEncrypted,
      location.pathname,
      location.search,
      sendCheckToWorker,
      getCheckInviteesComponent,
    ]
  )

  // Keep ref in sync so interval / visibility handlers use latest version
  useEffect(() => {
    performCheckRef.current = performCheck
  }, [performCheck])

  // Worker lifecycle — tạo 1 lần khi mount, terminate khi unmount
  useEffect(() => {
    const worker = new Worker(
      new URL("../workers/keyHealthWorker.ts", import.meta.url)
    )
    worker.onmessage = (e) => handleWorkerMessageRef.current?.(e)
    worker.onerror = (e) => console.error("[keyHealthWorker]", e.message)
    workerRef.current = worker
    return () => {
      worker.terminate()
      workerRef.current = null
    }
  }, [])

  // Worker message handler — nhận kết quả từ tất cả checks
  useEffect(() => {
    handleWorkerMessageRef.current = async (e: MessageEvent) => {
      workerCheckInFlightRef.current = false
      if (e.data.type !== "RESULT") return

      const failed: FailedKey[] = e.data.payload.failedKeys || []
      if (failed.length === 0) {
        setFixProgress([])
        setBannerStatus("green")
      } else {
        setBannerStatus("yellow")
        if (!autoFixRunningRef.current) {
          await runAutoFix(failed)
        }
      }

      // Sau khi fix xong, chỉ check đúng các ID đã gửi lên worker (từ URL)
      const { projectId, buildId, conversationId } = pendingCheckIdsRef.current
      const fallbackProjectId =
        location.pathname.match(/\/project-detail\/([^/]+)/)?.[1] || ""
      const effectiveProjectId = projectId

      const getKey = (storageKey: string, id?: string): boolean => {
        if (!id) return false
        const map: Record<string, string> = JSON.parse(
          localStorage.getItem(storageKey) || "{}"
        )
        return !!map[id]
      }
      const res = {
        project: effectiveProjectId
          ? {
              [effectiveProjectId]: getKey(
                EncryptionKeys.projectEncryptionKeys,
                effectiveProjectId
              ),
            }
          : {},
        build: buildId
          ? {
              [buildId]: getKey(
                EncryptionKeys.buildAdditionalInfoEncryptionKeys,
                buildId
              ),
            }
          : {},
        conversation: conversationId
          ? {
              [conversationId]: getKey(
                EncryptionKeys.conversationEncryptionKeys,
                conversationId
              ),
            }
          : {},
      }

      setKeyAvailability((prev) => ({
        project: { ...prev.project, ...res.project },
        build: { ...prev.build, ...res.build },
        conversation: { ...prev.conversation, ...res.conversation },
      }))
      setIsPreCheckReady(true)
    }
  }, [runAutoFix])

  // Initial check + 5-minute interval
  useEffect(() => {
    if (!userId) return
    performCheck()
    const interval = setInterval(
      () => performCheckRef.current?.(),
      5 * 60 * 1000
    )
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isEncrypted])

  // Route change re-check: mỗi page có thể dùng project key khác nhau.
  // hasHandledFirstPathEffectRef tránh double-check khi mount lần đầu.
  useEffect(() => {
    if (!userId) return
    if (!hasHandledFirstPathEffectRef.current) {
      hasHandledFirstPathEffectRef.current = true
      return
    }
    // Route change xóa cache + stale fix progress để tránh modal hiển thị dữ liệu cũ
    checkResultCacheRef.current = null
    setFixProgress([])
    setIsDetailModalOpen(false)
    // Block content ngay khi navigate vào pre-check pages
    const needsPreCheck =
      location.pathname.includes("project-detail") ||
      location.pathname.includes("project/component") ||
      location.pathname.includes("project/build-component") ||
      hasBuildPreCheckParams(location.pathname) ||
      location.pathname.includes("invitees/component") ||
      hasConversationPreCheckParams(location.pathname, location.search)
    if (needsPreCheck) {
      setIsPreCheckReady(false)
      setBannerScope(detectScopeFromPath(location.pathname))
    }
    performCheckRef.current?.()
  }, [location.pathname, location.search, userId])

  // Phase 1.7 — Tab visibility: chỉ re-check khi tab đã ẩn >5 phút
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        lastHiddenTimeRef.current = Date.now()
      } else {
        const hiddenDuration = Date.now() - lastHiddenTimeRef.current
        if (hiddenDuration > TAB_HIDDEN_THRESHOLD_MS) {
          performCheckRef.current?.(/* forceRefresh= */ true)
        }
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange)
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange)
  }, [])

  const requestVaultRecovery = useCallback(() => {
    setNeedsVaultRecovery(true)
  }, [])

  const onVaultRecoverySuccess = useCallback(() => {
    setNeedsVaultRecovery(false)
    performCheckRef.current?.()
  }, [])

  const onPrivateKeyRecoverySuccess = useCallback(() => {
    setNeedsPrivateKeyRecovery(false)
    performCheckRef.current?.()
  }, [])

  return {
    bannerStatus,
    bannerScope,
    fixProgress,
    isAutoFixing,
    isDetailModalOpen,
    openDetailModal: () => setIsDetailModalOpen(true),
    closeDetailModal: () => setIsDetailModalOpen(false),
    needsVaultRecovery,
    onVaultRecoverySuccess,
    requestVaultRecovery,
    needsPrivateKeyRecovery,
    onPrivateKeyRecoverySuccess,
    isPreCheckReady,
    keyAvailability,
  }
}
